/**
 * ============================================================================
 * AcknowledgeDialog — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: modal that captures the QA reviewer's attestation text + the
 *          single signature bundle id used to ACK the CC impact set.
 *
 * A11Y: role="dialog" + aria-modal, focus trap via Escape-to-close and an
 *  initial-focus close button.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './ImpactSetReview.module.css';
import { useAcknowledgeMutation } from './useAcknowledgeMutation';

export interface AcknowledgeDialogProps {
  changeId: string;
  itemCount: number;
  onClose: () => void;
  onAcknowledged: () => void;
}

const DEFAULT_ATTESTATION = (count: number) =>
  `I confirm that I have reviewed all ${count} impacted entities in this Change Control's Impact Set, and that the discovery rules and confidence assessments are appropriate.`;

export function AcknowledgeDialog({
  changeId,
  itemCount,
  onClose,
  onAcknowledged,
}: AcknowledgeDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const ackM = useAcknowledgeMutation();
  const [attestation, setAttestation] = useState(DEFAULT_ATTESTATION(itemCount));
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!attestation.trim()) {
      setError(
        t('changeControl.impactSet.errors.attestationRequired', {
          defaultValue: 'Attestation text is required.',
        }),
      );
      return;
    }
    if (!signature.trim()) {
      setError(
        t('changeControl.impactSet.errors.signatureRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    try {
      await ackM.mutateAsync({
        changeId,
        attestationText: attestation.trim(),
        signatureBundleId: signature.trim(),
      });
      onAcknowledged();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('changeControl.impactSet.errors.serverFailed', {
              defaultValue: 'Server rejected the action.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('changeControl.impactSet.acknowledge.title', {
              defaultValue: 'Acknowledge impact set',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('changeControl.impactSet.acknowledge.subtitle', {
              defaultValue:
                'Attest that you have reviewed every item in the impact set. This is recorded in the audit chain.',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="ack-attestation" className={dialogStyles.label}>
              {t('changeControl.impactSet.acknowledge.attestationLabel', {
                defaultValue: 'Attestation text',
              })}
              <span className={dialogStyles.required} aria-hidden="true">
                *
              </span>
            </label>
            <textarea
              id="ack-attestation"
              className={styles.dialogTextarea}
              value={attestation}
              rows={4}
              onChange={(e) => setAttestation(e.target.value)}
              required
            />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="ack-sig" className={dialogStyles.label}>
              {t('changeControl.impactSet.acknowledge.signatureLabel', {
                defaultValue: 'Signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="ack-sig"
              className={dialogStyles.input}
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              ref={closeRef}
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={ackM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={ackM.isPending}>
              {t('changeControl.impactSet.actions.acknowledge', {
                defaultValue: 'Acknowledge',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
